/**
 * RouteProgress, thin top-of-page progress bar shown while the route is
 * loading or while a tracked action is in flight (e.g. sign-in → /home
 * transition).
 *
 * Hooks into router via useLocation; whenever pathname changes, briefly
 * shows the bar. Also exposes useRouteProgress() so any component can
 * start/stop the bar manually (used by SignIn for the sign-in → fetch
 * /api/me → redirect chain that otherwise looks frozen for 600-1500ms).
 *
 * Per Ean's 2026-06-03 feedback (#1): the gap between "Sign in" click and
 * /home render felt dead. The button spinner stops at the moment the
 * fetch returns 200; the next two steps (refresh, navigate) happen in
 * React state and weren't visible. A top progress bar bridges that gap.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

interface RouteProgressValue {
  start: () => void;
  done: () => void;
}

const RouteProgressContext = createContext<RouteProgressValue | undefined>(undefined);

/** Provider, mounts the visual bar + exposes start/done to descendants. */
export function RouteProgressProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);
  const [width, setWidth] = useState(0);
  const location = useLocation();

  const start = useCallback(() => setCount((c) => c + 1), []);
  const done = useCallback(() => setCount((c) => Math.max(0, c - 1)), []);

  // Auto-tick the bar to ~85% while active; jump to 100% when done.
  useEffect(() => {
    if (count > 0) {
      setWidth(15);
      let pct = 15;
      const id = setInterval(() => {
        pct = Math.min(85, pct + (90 - pct) * 0.15);
        setWidth(pct);
      }, 120);
      return () => clearInterval(id);
    } else if (width > 0) {
      // Finish the bar, then fade out.
      setWidth(100);
      const id = setTimeout(() => setWidth(0), 220);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [count]);

  // Show a brief bar on every route change too (cheap "we navigated" cue).
  useEffect(() => {
    setCount((c) => c + 1);
    // Settle exactly once: whichever of the timeout or cleanup runs first
    // performs the matching decrement. Previously cleanup cancelled the
    // pending decrement, so a second navigation within 280ms (e.g. the
    // initial "/" -> "/sign-in" redirect, or StrictMode effect replay) left
    // the counter permanently > 0 and the bar stuck at ~85%.
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      setCount((c) => Math.max(0, c - 1));
    };
    const id = setTimeout(settle, 280);
    return () => {
      clearTimeout(id);
      settle();
    };
  }, [location.pathname]);

  return (
    <RouteProgressContext.Provider value={{ start, done }}>
      <div
        className="route-progress"
        role="progressbar"
        aria-hidden={width === 0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(width)}
        style={{
          width: `${width}%`,
          opacity: width === 0 ? 0 : 1,
        }}
      />
      {children}
    </RouteProgressContext.Provider>
  );
}

export function useRouteProgress(): RouteProgressValue {
  const ctx = useContext(RouteProgressContext);
  // Tolerate calls before the provider is mounted (e.g. tests).
  if (ctx === undefined) {
    return { start: () => {}, done: () => {} };
  }
  return ctx;
}
